import { FC, useRef, useState } from 'react';
import { CheckBox } from 'src/components/ui/CheckBox';
import { useClickOutside } from 'src/hooks/useClickOutside';
import { DashboardTableHeadFilter } from 'src/components/Dashboard/DashboardTableHeadFilter';
import { useAuth } from 'src/hooks/useAuth';
import { setCheckedPolygons } from 'src/store/polygons';
import { dashboardTableItems } from 'src/config/data';
import { useAppDispatch, useAppSelector } from 'src/hooks/useRedux';
import { setIsAllChecked } from 'src/store/common';
import {
  DashboardPolygonsFilterType,
  DashboardTableSort,
  PolygonsFilterType,
  PolygonType,
} from 'src/config/types';

import { ReactComponent as SortIcon } from 'src/assets/icons/kit/sort.svg';
import { ReactComponent as FilterIcon } from 'src/assets/icons/kit/filter.svg';
import cn from 'classnames';

interface DashboardTableHeadProps {
  polygons: PolygonType[];
  sort: DashboardTableSort | undefined;
  setSort: (arg: DashboardTableSort | undefined) => void;
}

export const DashboardTableHead: FC<DashboardTableHeadProps> = ({ polygons, sort, setSort }) => {
  const dispatch = useAppDispatch();
  const { polygonsFilter, dashboardPolygonsFilter } = useAppSelector(state => state.filters);
  const { isAllChecked } = useAppSelector(state => state.common);
  const { user } = useAuth();
  const ref = useRef<HTMLDivElement>(null);

  const [openFilter, setOpenFilter] = useState<
    keyof PolygonsFilterType | keyof DashboardPolygonsFilterType
  >();

  useClickOutside(ref, () => setOpenFilter(undefined));

  const handleClickAllChecked = () => {
    if (!(user?.isLeader || user?.isManager)) return;

    if (isAllChecked) {
      dispatch(setIsAllChecked(false));
      dispatch(setCheckedPolygons([]));
    } else {
      dispatch(setIsAllChecked(true));
      dispatch(setCheckedPolygons(polygons));
    }
  };

  const handleSort = (field: keyof PolygonType) => {
    setSort({
      type: field === sort?.field ? (sort?.type === 'ASC' ? 'DESC' : 'ASC') : 'ASC',
      field,
    });
  };

  const isActiveFilter = name =>
    (name && polygonsFilter?.[name]?.length) || (name && dashboardPolygonsFilter?.[name]?.length);

  return (
    <div
      className={cn(
        'grid w-fit t-lg:w-auto min-h-[2rem] t-lg:min-h-[3.5rem] border-b border-grey-1100 dark:border-white rounded-t ' +
          'grid-flow-col auto-cols-min items-center' +
          'min-h-[2rem] h-[2rem] t-lg:min-h-[3.5rem] t-lg:h-[3.5rem] py-1 flex items-center group',
        {
          'bg-grey-2200 dark:bg-blue-700': Boolean(openFilter),
          'bg-white dark:bg-blue-200': !openFilter,
        },
      )}
    >
      {dashboardTableItems.map((item, i) => (
        <div
          key={`dashboard-table-head-${i}`}
          className={cn(
            'relative flex items-center justify-start h-8 t-lg:h-14 border-grey-1100 dark:border-white',
            item.className,
            'group',
            {
              ' cursor-pointer': Boolean(item?.filter) || Boolean(item?.dashboardFilter),
              'bg-white dark:bg-blue-800 shadow-blacks-800':
                (item?.filter && item?.filter === openFilter) ||
                (item?.dashboardFilter && item?.dashboardFilter === openFilter),
            },
          )}
        >
          {item.label === 'checkBox' ? (
            <CheckBox isActive={isAllChecked} onClick={handleClickAllChecked} />
          ) : item.label === 'arrow' ? null : (
            <div
              className="flex items-center gap-1 dark:text-white text-ultra-xs t-lg:text-xs whitespace-nowrap font-gothampro-400"
              onClick={() =>
                item.filter || item.dashboardFilter
                  ? setOpenFilter(
                      openFilter === (item.filter || item.dashboardFilter)
                        ? undefined
                        : item.filter || item.dashboardFilter,
                    )
                  : undefined
              }
            >
              {item.sort && (
                <SortIcon
                  onClick={e => {
                    e.stopPropagation();
                    item.sort && handleSort(item.sort);
                  }}
                  className={cn('w-3.5 h-3.5 cursor-pointer transition-transform', {
                    'rotate-180': sort?.field === item.sort && sort.type === 'ASC',
                    'fill-orange-100': sort?.field === item.sort,
                    'fill-grey-2600 hover:fill-orange-100': sort?.field !== item.sort,
                  })}
                />
              )}
              {item.label}
              <FilterIcon
                className={cn('w-3 h-3 t-lg:w-4 t-lg:h-4 transition-all', 'hover:fill-blue-100', {
                  'fill-blue-100':
                    openFilter === (item.filter || item.dashboardFilter) ||
                    isActiveFilter(item.filter || item.dashboardFilter),

                  'fill-grey-2600':
                    openFilter !== (item.filter || item.dashboardFilter) &&
                    !isActiveFilter(item.filter || item.dashboardFilter),
                })}
              />
            </div>
          )}

          {openFilter === item.filter && item.filter && (
            <div ref={ref}>
              <DashboardTableHeadFilter filter={item.filter} />
            </div>
          )}
          {openFilter === item.dashboardFilter && item.dashboardFilter && (
            <div ref={ref}>
              <DashboardTableHeadFilter filter={item.dashboardFilter} isDashboard />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
